import React, { useState } from 'react'

import { Eye, EyeOff, AlertCircle, Loader2 } from 'lucide-react'

import { userService } from '@/core/api/services/userService'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { Input } from '@/shared/components/atoms/Input'
import { Label } from '@/shared/components/atoms/Label/Label'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { extractErrorMessage } from '@/shared/utils/helpers'

interface ChangePasswordModalProps {
	isOpen: boolean
	onClose: () => void
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
	isOpen,
	onClose,
}) => {
	const { t } = useTranslation('common')
	const [formData, setFormData] = useState({
		oldPassword: '',
		newPassword: '',
		confirmPassword: '',
	})
	const [showPasswords, setShowPasswords] = useState({
		old: false,
		new: false,
		confirm: false,
	})
	const [errors, setErrors] = useState<{
		passwordMismatch?: string
		passwordRequirements?: string[]
		apiError?: string
	}>({})
	const [isLoading, setIsLoading] = useState(false)
	const { showNotification } = useNotificationContext()

	const handleInputChange = (field: string, value: string) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}))

		// Clear errors when user starts typing
		if (errors.passwordMismatch || errors.apiError) {
			setErrors((prev) => ({
				...prev,
				passwordMismatch: undefined,
				apiError: undefined,
			}))
		}
	}

	const validatePassword = (password: string) => {
		const requirements = []
		if (password.length < 8)
			requirements.push(
				t('account.user_profile.password.requirements.min_length')
			)
		if (!/\d/.test(password))
			requirements.push(
				t('account.user_profile.password.requirements.has_number')
			)
		if (!/[!@#$%^&*(),.?":{}|<>]/.test(password))
			requirements.push(
				t('account.user_profile.password.requirements.has_special_character')
			)
		return requirements
	}

	const handleSubmit = async () => {
		const newErrors: typeof errors = {}

		// Check if passwords match
		if (formData.newPassword !== formData.confirmPassword) {
			newErrors.passwordMismatch = t(
				'account.user_profile.password.requirements.mismatch'
			)
		}

		// Check password requirements
		const passwordRequirements = validatePassword(formData.newPassword)
		if (passwordRequirements.length > 0) {
			newErrors.passwordRequirements = passwordRequirements
		}

		setErrors(newErrors)

		// If no errors, proceed with password change
		if (Object.keys(newErrors).length === 0) {
			setIsLoading(true)
			try {
				await userService.changePassword({
					password: formData.oldPassword,
					new_password: formData.newPassword,
				})

				showNotification({
					type: 'success',
					message: t('account.user_profile.password.changed_successfully'),
					description: t(
						'account.user_profile.password.updated_successfully_description'
					),
				})

				handleClose()
			} catch (error: any) {
				const errorMessage = extractErrorMessage(
					error,
					t('account.user_profile.password.failed_to_change_password')
				)
				setErrors((prev) => ({ ...prev, apiError: errorMessage }))
			} finally {
				setIsLoading(false)
			}
		}
	}

	const handleClose = () => {
		if (isLoading) return // Prevent closing while loading

		setFormData({
			oldPassword: '',
			newPassword: '',
			confirmPassword: '',
		})
		setErrors({})
		setShowPasswords({
			old: false,
			new: false,
			confirm: false,
		})
		setIsLoading(false)
		onClose()
	}

	const togglePasswordVisibility = (field: 'old' | 'new' | 'confirm') => {
		setShowPasswords((prev) => ({
			...prev,
			[field]: !prev[field],
		}))
	}

	const isFormValid =
		formData.oldPassword &&
		formData.newPassword &&
		formData.confirmPassword &&
		!isLoading

	return (
		<Dialog open={isOpen} onOpenChange={handleClose}>
			<DialogContent className="w-[452px]">
				<DialogHeader className="text-left md:text-center">
					<DialogTitle className="text-sm">
						{t('account.user_profile.change_password')}
					</DialogTitle>
				</DialogHeader>

				{/* Divider line */}
				<div className="border-t border-border"></div>

				<div className="space-y-6 md:space-y-8 text-xs">
					{/* Old password */}
					<div className="flex flex-col gap-2">
						<Label htmlFor="oldPassword">
							{t('account.user_profile.password.old_password')}
						</Label>
						<div className="relative">
							<Input
								id="oldPassword"
								name="oldPassword"
								type={showPasswords.old ? 'text' : 'password'}
								value={formData.oldPassword}
								onChange={(e) =>
									handleInputChange('oldPassword', e.target.value)
								}
								placeholder={t(
									'account.user_profile.password.old_password_placeholder'
								)}
								autoComplete="new-password"
							/>
							<Button
								type="button"
								variant="ghost"
								size="sm"
								className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent cursor-pointer"
								onClick={() => togglePasswordVisibility('old')}
							>
								{showPasswords.old ? (
									<EyeOff className="h-4 w-4" />
								) : (
									<Eye className="h-4 w-4" />
								)}
							</Button>
						</div>
					</div>

					{/* New password */}
					<div className="flex flex-col gap-2">
						<Label htmlFor="newPassword">
							{t('account.user_profile.password.new_password')}
						</Label>
						<div className="relative">
							<Input
								id="newPassword"
								name="newPassword"
								type={showPasswords.new ? 'text' : 'password'}
								value={formData.newPassword}
								onChange={(e) =>
									handleInputChange('newPassword', e.target.value)
								}
								placeholder={t(
									'account.user_profile.password.new_password_placeholder'
								)}
								autoComplete="new-password"
							/>
							<Button
								type="button"
								variant="ghost"
								size="sm"
								className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent cursor-pointer"
								onClick={() => togglePasswordVisibility('new')}
							>
								{showPasswords.new ? (
									<EyeOff className="h-4 w-4" />
								) : (
									<Eye className="h-4 w-4" />
								)}
							</Button>
						</div>

						{/* Password requirements */}
						<div className="flex md:flex-row flex-col md:gap-4 gap-2 text-xs text-muted-foreground">
							<div
								className={
									formData.newPassword.length >= 8 ? 'text-green-600' : ''
								}
							>
								{t('account.user_profile.password.requirements.min_length')}
							</div>
							<div
								className={
									/\d/.test(formData.newPassword) ? 'text-green-600' : ''
								}
							>
								{t('account.user_profile.password.requirements.has_number')}
							</div>
							<div
								className={
									/[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword)
										? 'text-green-600'
										: ''
								}
							>
								{t(
									'account.user_profile.password.requirements.has_special_character'
								)}
							</div>
						</div>
					</div>

					{/* Confirm new password */}
					<div className="flex flex-col gap-2">
						<Label htmlFor="confirmPassword">
							{t('account.user_profile.password.confirm_password')}
						</Label>
						<div className="relative">
							<Input
								id="confirmPassword"
								name="confirmPassword"
								type={showPasswords.confirm ? 'text' : 'password'}
								value={formData.confirmPassword}
								onChange={(e) =>
									handleInputChange('confirmPassword', e.target.value)
								}
								placeholder={t(
									'account.user_profile.password.confirm_password_placeholder'
								)}
								autoComplete="new-password"
							/>
							<Button
								type="button"
								variant="ghost"
								size="sm"
								className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent cursor-pointer"
								onClick={() => togglePasswordVisibility('confirm')}
							>
								{showPasswords.confirm ? (
									<EyeOff className="h-4 w-4" />
								) : (
									<Eye className="h-4 w-4" />
								)}
							</Button>
						</div>
					</div>

					{/* Error messages */}
					{errors.passwordMismatch && (
						<div className="flex items-center gap-2 text-xs text-red-600">
							<AlertCircle className="h-4 w-4" />
							{errors.passwordMismatch}
						</div>
					)}

					{errors.passwordRequirements &&
						errors.passwordRequirements.length > 0 && (
							<div className="space-y-1">
								{errors.passwordRequirements.map((requirement, index) => (
									<div
										key={index}
										className="flex items-center gap-2 text-xs text-red-600"
									>
										<AlertCircle className="h-4 w-4" />
										{requirement}
									</div>
								))}
							</div>
						)}

					{errors.apiError && (
						<div className="flex items-center gap-2 text-xs text-red-600">
							<AlertCircle className="h-4 w-4" />
							{errors.apiError}
						</div>
					)}

					{/* Action buttons */}
					<div className="flex flex-col-reverse md:flex-row justify-end gap-3 pt-4">
						<Button
							variant="outline"
							className="rounded-full cursor-pointer"
							onClick={handleClose}
							disabled={isLoading}
						>
							{t('actions.cancel')}
						</Button>
						<Button
							className="rounded-full cursor-pointer"
							onClick={handleSubmit}
							disabled={!isFormValid}
						>
							{isLoading ? (
								<>
									<Loader2 className="w-4 h-4 mr-2 animate-spin" />
									{t('account.user_profile.password.changing_password')}
								</>
							) : (
								t('account.user_profile.password.change_password')
							)}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default ChangePasswordModal
